import React from "react";
import { Heart, Crown, Sparkles, ChevronRight, RefreshCw, ArrowLeft, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/PhotoGallery.css";
import { Photo } from "../../../shared/types/api";
import LazyImage from "../../../shared/components/ui/media/LazyImage";
import PhotoModal from "../../../shared/components/ui/overlays/PhotoModal";
import { usePhotoGalleryPaginated } from "../hooks/usePhotoGalleryPaginated";
import { useModal } from "../../../shared/hooks/ui/useModal";
import { usePhotoLike } from "../../../shared/hooks/data/usePhotoLike";
import { useErrorHandler } from "../../../shared/hooks/utils/useErrorHandler";

/**
 * リファクタリング後のゲスト向け写真ギャラリーページ
 * - 共通コンポーネント・フック使用
 * - コード行数を大幅削減
 * - 保守性・可読性の向上
 */
export default function PhotoGallery() {
  const navigate = useNavigate();

  // カスタムフック
  const { error: appError, handleError, clearError: clearAppError } = useErrorHandler();
  const photoModal = useModal<Photo>();
  const { handleLike: processLike, isProcessing: isLikeProcessing } = usePhotoLike();

  // ページネーション対応カスタムフック
  const {
    photos,
    filter,
    loading,
    error,
    isLoadingMore,
    hasNextPage,
    totalElements,
    loadMore,
    handleLike: originalHandleLike,
    changeFilter,
    manualRefresh,
    isRoomSet,
  } = usePhotoGalleryPaginated({
    autoReload: {
      onVisibilityChange: true,
      onImageUpdate: true,
    },
    pageSize: 16,
  });

  // 統一されたいいね処理（usePhotoGalleryPaginatedのhandleLikeのみ使用）
  const handleOptimizedLike = async (photoId: string) => {
    if (isLikeProcessing) return;

    try {
      console.log('[PhotoGallery] Like button clicked for photo:', photoId);
      await originalHandleLike(photoId);
    } catch (err) {
      console.error('[PhotoGallery] Like processing failed:', err);
      handleError(err, 'いいね処理');
    }
  };

  // ユーザーメニューに戻る関数
  const handleBackToUserMenu = () => {
    navigate('/user/menu');
  };


  // ローディング表示
  if (loading) {
    return (
      <div className="photo-gallery-container">
        <div className="loading-container">
          <div className="loading-spinner">
          </div>
          <Crown className="loading-icon" />
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="photo-gallery-container">
        <div className="error-container">
          <div className="error-content">
            <Crown className="error-icon" />
            <h3>読み込みエラー</h3>
            <p>{error}</p>
            {!isRoomSet && (
              <p className="error-note">
                正しいゲストログインページからアクセスしてください。
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-gallery-container">
      {/* ヘッダーセクション */}
      <div className="gallery-header">
        <div className="header-decoration">
          <Crown className="header-crown" />
          <Sparkles className="header-sparkle sparkle-1" />
          <Sparkles className="header-sparkle sparkle-2" />
        </div>
        
        <h1 className="gallery-title">
          <Heart className="title-heart" />
          みんなの思い出
          <Heart className="title-heart" />
        </h1>
        
        <p className="gallery-subtitle">
          特別な一日をゲストの皆様と共有された美しい瞬間
        </p>
        


        {/* フィルターボタンと更新ボタン */}
        <div className="filter-buttons">
          {/* 戻るボタン */}
          <button 
            className="filter-button back-button"
            onClick={handleBackToUserMenu}
            title="ユーザーメニューに戻る"
          >
            <ArrowLeft size={16} />
            戻る
          </button>
          <button 
            className={`filter-button ${filter === 'recent' ? 'active' : ''}`}
            onClick={() => changeFilter('recent')}
          >
            最新順
          </button>
          <button 
            className={`filter-button ${filter === 'popular' ? 'active' : ''}`}
            onClick={() => changeFilter('popular')}
          >
            人気順
          </button>
          
          {/* 手動更新ボタン */}
          <button 
            className={`filter-button refresh-button ${loading ? 'loading' : ''}`}
            onClick={manualRefresh}
            disabled={loading}
            title="写真を最新の状態に更新"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            更新
          </button>
        </div>
      </div>

      {/* 写真ギャラリーグリッド */}
      <div className="photos-grid">
        {photos.map((photo) => (
          <div 
            key={photo.id} 
            className="photo-card"
            onClick={() => photoModal.openModal(photo)}
            style={{ cursor: 'pointer' }}
          >
            <div className="photo-image-container">
              <LazyImage
                src={photo.imageUrl}
                thumbnailSrc={photo.thumbnailUrl}
                alt="写真"
                className="photo-image"
              />
              <div className="photo-overlay">
                <div className="overlay-content">
                  <Heart size={24} className="overlay-heart" />
                  <span>詳細を見る</span>
                </div>
              </div>
            </div>
            
            <div className="photo-info">
              <div className="photo-meta">
                <div className="photo-date">
                  <Calendar size={14} />
                  <span>{new Date(photo.uploadDate).toLocaleDateString('ja-JP')}</span>
                </div>
              </div>
              
              <button 
                className={`like-button ${photo.isLiked ? 'liked' : ''} ${loading ? 'processing' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('[PhotoGallery] Like button clicked for photo:', photo.id, 'current state:', { likes: photo.likes, isLiked: photo.isLiked });
                  handleOptimizedLike(photo.id);
                }}
                disabled={loading}
                title={photo.isLiked ? 'いいねを取り消す' : 'いいねする'}
              >
                <Heart size={16} fill={photo.isLiked ? 'currentColor' : 'none'} />
                <span>{photo.likes}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 無限スクロール対応ロードモアボタン */}
      {hasNextPage && (
        <div className="load-more-container">
          <button 
            className={`load-more-button ${isLoadingMore ? 'loading' : ''}`}
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <RefreshCw size={16} className="spinning" />
                読み込み中...
              </>
            ) : (
              <>
                <ChevronRight size={16} />
                もっと見る
              </>
            )}
          </button>
          
          {/* 写真総数表示 */}
          <div className="photo-count-info">
            <span>{photos.length} / {totalElements}枚表示中</span>
          </div>
        </div>
      )}

      {/* 写真がない場合 */}
      {photos.length === 0 && (
        <div className="empty-state">
          <Heart size={60} className="empty-heart" />
          <h3>まだ写真がありません</h3>
          <p>ゲストの皆様からの素敵な写真をお待ちしています</p>
        </div>
      )}

      {/* 写真モーダル */}
      <PhotoModal 
        photo={photoModal.data ? photos.find(p => p.id === photoModal.data?.id) || photoModal.data : null}
        onClose={photoModal.closeModal}
        onLike={handleOptimizedLike}
      />

      {/* エラー表示 */}
      {appError && (
        <div className="error-toast">
          <p>{appError.message}</p>
          <button onClick={clearAppError}>閉じる</button>
        </div>
      )}
    </div>
  );
}